import {
  BarChartOutlined,
  CalendarMonthOutlined,
  PaidOutlined,
  ReceiptLongOutlined,
  TrendingUpOutlined,
} from '@mui/icons-material';
import { CircularProgress, Grid, Typography } from '@mui/material';
import { hexToRGBA } from 'lib/designSystem/BarChart';
import EnhacedBox from 'lib/designSystem/Box';
import StatCard from 'lib/designSystem/Charts/StatCard';
import { formatMoney } from 'lib/functions/utils';
import { TenderType } from 'lib/types/models';
import React from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

const TENDER_TYPE_COLORS: Record<TenderType, string> = {
  [TenderType.L1]: '#3B82F6',
  [TenderType.LE]: '#10B981',
  [TenderType.LP]: '#F59E0B',
  [TenderType.LQ]: '#EF4444',
  [TenderType.LR]: '#06B6D4',
  [TenderType.E2]: '#8B5CF6',
  [TenderType.CO]: '#F97316',
  [TenderType.B2]: '#6366F1',
  [TenderType.H2]: '#14B8A6',
  [TenderType.I2]: '#84CC16',
  [TenderType.LS]: '#F43F5E',
};

const FALLBACK_COLORS = ['#94A3B8', '#CBD5E1', '#E2E8F0', '#F1F5F9', '#64748B'];

const getTenderTypeColor = (tenderType: string, index: number = 0): string => {
  if (tenderType in TENDER_TYPE_COLORS) {
    return TENDER_TYPE_COLORS[tenderType as TenderType];
  }
  return FALLBACK_COLORS[index % FALLBACK_COLORS.length];
};

type ChartRow = { name: string } & Record<string, number | string>;
export type StatsResponse = {
  monthlyAwardedValue: ChartRow[];
  tendersCountByMonth: ChartRow[];
  monthsOperating: number;
  totalAmount: number;
  totalCount: number;
};

export const TendersStats: React.FC<{
  stats: StatsResponse | null;
  loading?: boolean;
}> = ({ stats, loading = false }) => {
  if (loading) {
    return (
      <Grid container spacing={1.5} sx={{ my: 1 }}>
        <Grid item xs={12}>
          <EnhacedBox margin="0px">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '40px',
                flexDirection: 'column',
                gap: '16px',
              }}
            >
              <CircularProgress size={40} />
              <Typography variant="body1" color="text.secondary">
                Cargando estadísticas...
              </Typography>
            </div>
          </EnhacedBox>
        </Grid>
      </Grid>
    );
  }

  if (!stats) {
    return (
      <Grid container spacing={1.5} sx={{ my: 1 }}>
        <Grid item xs={12}>
          <EnhacedBox margin="0px">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '40px',
                flexDirection: 'column',
                gap: '16px',
                textAlign: 'center',
              }}
            >
              <Typography variant="h6" color="text.secondary">
                Sin datos disponibles
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No se encontraron estadísticas de licitaciones para esta
                empresa.
                <br />
                Esto puede deberse a que no hay licitaciones adjudicadas o los
                datos aún no han sido procesados.
              </Typography>
            </div>
          </EnhacedBox>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={1.5} sx={{ my: 1 }}>
      <Grid item xs={12} sm={6} md={4}>
        <StatCard
          label="Monto Adjudicado Total"
          value={formatMoney(stats.totalAmount)}
          valueLabel="CLP"
          color="#3B82F6"
          icon={<PaidOutlined sx={{ fontSize: 22 }} />}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <StatCard
          label="Licitaciones Adjudicadas"
          value={stats.totalCount}
          valueLabel="licitaciones"
          color="#10B981"
          icon={<ReceiptLongOutlined sx={{ fontSize: 22 }} />}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <StatCard
          label="Meses Operando"
          value={stats.monthsOperating}
          valueLabel="meses"
          color="#F59E0B"
          icon={<CalendarMonthOutlined sx={{ fontSize: 22 }} />}
        />
      </Grid>

      <Grid item xs={12}>
        <EnhacedBox margin="0px">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 16,
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: hexToRGBA('#3B82F6', 0.12),
              }}
            >
              <TrendingUpOutlined sx={{ fontSize: 18, color: '#3B82F6' }} />
            </div>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                fontSize: '1.1rem',
              }}
            >
              Monto Adjudicado Mensual
            </Typography>
          </div>
          <CustomLineChart data={stats.monthlyAwardedValue} aspect={3} />
          {(!stats.monthlyAwardedValue ||
            stats.monthlyAwardedValue.length === 0) && (
            <div
              style={{
                textAlign: 'center',
                padding: '16px',
                color: '#666',
                fontSize: '14px',
                borderTop: '1px solid #f0f0f0',
                marginTop: '16px',
              }}
            >
              No hay datos de monto adjudicado mensual disponibles
            </div>
          )}
        </EnhacedBox>
      </Grid>
      <Grid item xs={12}>
        <EnhacedBox margin="0px">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 16,
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: hexToRGBA('#10B981', 0.12),
              }}
            >
              <BarChartOutlined sx={{ fontSize: 18, color: '#10B981' }} />
            </div>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                fontSize: '1.1rem',
              }}
            >
              Licitaciones Adjudicadas Mensuales
            </Typography>
          </div>
          <CustomBarChart data={stats.tendersCountByMonth} />
          {(!stats.tendersCountByMonth ||
            stats.tendersCountByMonth.length === 0) && (
            <div
              style={{
                textAlign: 'center',
                padding: '16px',
                color: '#666',
                fontSize: '14px',
                borderTop: '1px solid #f0f0f0',
                marginTop: '16px',
              }}
            >
              No hay datos de licitaciones adjudicadas mensuales disponibles
            </div>
          )}
        </EnhacedBox>
      </Grid>
    </Grid>
  );
};

const CustomBarChart: React.FC<{ data: ChartRow[] }> = ({ data }) => {
  // Early return if no data
  if (!data || data.length === 0) {
    return (
      <div
        style={{
          height: 300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#666',
          fontSize: '14px',
        }}
      >
        No hay datos disponibles para mostrar
      </div>
    );
  }

  const getDataKeys = (data: ChartRow[]) => {
    const allNumericKeys = new Set<string>();

    data.forEach((row) => {
      Object.keys(row).forEach((key) => {
        if (key !== 'name' && typeof row[key] === 'number') {
          allNumericKeys.add(key);
        }
      });
    });

    return Array.from(allNumericKeys);
  };

  const dataKeys = getDataKeys(data);

  // If no numeric keys found, show message
  if (dataKeys.length === 0) {
    return (
      <div
        style={{
          height: 300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#666',
          fontSize: '14px',
        }}
      >
        No se encontraron datos numéricos para graficar
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis
          dataKey="name"
          tick={{ fontSize: 12, fill: '#666' }}
          axisLine={{ stroke: '#ddd' }}
        />
        <YAxis
          tick={{ fontSize: 12, fill: '#666' }}
          axisLine={{ stroke: '#ddd' }}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: '#fff',
            border: '1px solid #ddd',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
          formatter={(value: number, name: string) => [
            `${value} licitaciones`,
            name,
          ]}
          labelFormatter={(label) => `Mes: ${label}`}
        />
        <Legend verticalAlign="top" height={36} iconType="circle" />
        {dataKeys.map((key, index) => (
          <Bar
            key={key}
            dataKey={key}
            fill={getTenderTypeColor(key, index)}
            name={key === 'total' ? 'Total' : key}
            radius={[4, 4, 0, 0]}
            stackId="a"
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

const CustomLineChart: React.FC<{ data: ChartRow[]; aspect: number }> = ({
  data,
}) => {
  if (!data || data.length === 0) {
    return (
      <div
        style={{
          height: 300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#666',
          fontSize: '14px',
        }}
      >
        No hay datos disponibles para mostrar
      </div>
    );
  }

  const getDataKeys = (data: ChartRow[]) => {
    const allNumericKeys = new Set<string>();

    data.forEach((row) => {
      Object.keys(row).forEach((key) => {
        if (key !== 'name' && typeof row[key] === 'number') {
          allNumericKeys.add(key);
        }
      });
    });

    return Array.from(allNumericKeys);
  };

  const dataKeys = getDataKeys(data);

  // If no numeric keys found, show message
  if (dataKeys.length === 0) {
    return (
      <div
        style={{
          height: 300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#666',
          fontSize: '14px',
        }}
      >
        No se encontraron datos numéricos para graficar
      </div>
    );
  }

  const formatYAxisValue = (value: number) => {
    if (value >= 1000000000) return `${(value / 1000000000).toFixed(1)}B`;
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value.toString();
  };

  const formatTooltipValue = (value: number) => {
    return formatMoney(value);
  };

  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis
          dataKey="name"
          tick={{ fontSize: 12, fill: '#666' }}
          axisLine={{ stroke: '#ddd' }}
        />
        <YAxis
          tick={{ fontSize: 12, fill: '#666' }}
          tickFormatter={formatYAxisValue}
          axisLine={{ stroke: '#ddd' }}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: '#fff',
            border: '1px solid #ddd',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
          formatter={(value: number, name: string) => [
            formatTooltipValue(value),
            name,
          ]}
          labelFormatter={(label) => `Mes: ${label}`}
        />
        <Legend verticalAlign="top" height={36} iconType="circle" />
        {dataKeys.map((key, index) => (
          <Line
            key={key}
            type="monotone"
            dataKey={key}
            stroke={getTenderTypeColor(key, index)}
            strokeWidth={2}
            dot={{ r: 4, strokeWidth: 2 }}
            activeDot={{ r: 6, strokeWidth: 2 }}
            name={key === 'total' ? 'Total' : key}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};
